import React, { useState, useEffect } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
} from "firebase/auth";
import { auth } from "../config/firebase";
import { useNavigate } from "react-router-dom";
import {
  signInWithGooglePopup,
  handleRedirectResult,
  initializeAuthPersistence,
} from "../services/oauthAuth";
import { useUserRole } from "../hooks/useUserRole";
import { OnboardingModal } from "../components/OnboardingModal";
import "../styles/Auth.css";

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [isSignUp, setIsSignUp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Track onboarding modal state
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const { onboardingCompleted, loading: roleLoading, role, tenant_id } = useUserRole();

  // Initialize auth and handle redirect results on mount
  useEffect(() => {
    const init = async () => {
      await initializeAuthPersistence();
      const result = await handleRedirectResult();
      if (result) {
        // User came back from OAuth redirect - check onboarding
        // If onboarding not complete, show modal
      }
    };
    init();
  }, []);

  // Check if already authenticated on page load - redirect immediately
  useEffect(() => {
    if (auth.currentUser) {
      console.log('[LoginPage] User already authenticated:', auth.currentUser.email);
      // Just navigate directly - let the role load hook handle redirection
      if (role && !roleLoading) {
        console.log('[LoginPage] Role loaded, redirecting:', role);
        if (role === 'END_USER') {
          navigate("/wallet");
        } else if (role === 'KIOSK_ADMIN') {
          navigate("/qr-scanner");
        } else {
          navigate("/admin");
        }
      }
    }
  }, [auth.currentUser, role, roleLoading, navigate]);

  // After user logs in and role loads, redirect or show onboarding
  useEffect(() => {
    if (!roleLoading && auth.currentUser) {
      if (!onboardingCompleted) {
        setShowOnboardingModal(true);
      } else {
        // Onboarding already complete - redirect immediately
        if (role === 'END_USER') {
          navigate("/wallet");
        } else if (role === 'KIOSK_ADMIN') {
          navigate("/qr-scanner");
        } else {
          // ADMIN, CLUB, SUPER_ADMIN -> go to admin dashboard
          navigate("/admin");
        }
      }
    }
  }, [roleLoading, onboardingCompleted, role, navigate]);

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (isSignUp) {
        // Sign up
        const result = await createUserWithEmailAndPassword(auth, email, password);
        if (displayName) {
          await updateProfile(result.user, { displayName });
        }
        // After signup, modal will show automatically via useEffect
      } else {
        // Sign in
        await signInWithEmailAndPassword(auth, email, password);
        // After signin, modal will show automatically via useEffect if onboarding not complete
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Authentication failed";
      setError(message);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      await signInWithGooglePopup();
      // After OAuth, modal will show automatically via useEffect if onboarding not complete
    } catch (err) {
      const message = err instanceof Error ? err.message : "Google sign-in failed";
      setError(message);
      setLoading(false);
    }
  };

  // Handle successful onboarding
  const handleOnboardingComplete = () => {
    setShowOnboardingModal(false);
    
    // Redirect based on role
    if (role === 'END_USER') {
      navigate("/wallet");
    } else if (role === 'KIOSK_ADMIN') {
      navigate("/qr-scanner");
    } else {
      // ADMIN, CLUB_ADMIN, SUPER_ADMIN -> go to admin dashboard
      navigate("/admin");
    }
  };

  return (
    <>
      <div className="auth-container">
        <div className="auth-card">
          <h1>Gavekort Wallet</h1>
          <p className="subtitle">Gift Card & Value Management</p>

          {/* OAuth2 Providers Section */}
          <div className="oauth-providers-section">
            <p className="oauth-label">Logg inn med:</p>
            
            <div className="oauth-buttons-grid">
              {/* Google Sign-In Button */}
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                className="oauth-button oauth-google"
                title="Logg inn med Google-kontoen din"
              >
                <svg
                  className="oauth-icon"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                <span>Google</span>
              </button>
            </div>
          </div>

          <div className="divider">
            <span>eller</span>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailSubmit} className="auth-form">
            {isSignUp && (
              <div className="form-group">
                <label htmlFor="displayName">Display Name</label>
                <input
                  id="displayName"
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="John Doe"
                  required={isSignUp}
                />
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="password-input-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="show-password-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? "🙈 Hide" : "👁️ Show"}
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" disabled={loading} className="submit-button">
              {loading ? "Loading..." : isSignUp ? "Sign Up" : "Sign In"}
            </button>
          </form>

          <div className="auth-toggle">
            <p>
              {isSignUp ? "Already have an account?" : "Don't have an account?"}
              <button
                type="button"
                onClick={() => setIsSignUp(!isSignUp)}
                className="toggle-button"
              >
                {isSignUp ? "Sign In" : "Sign Up"}
              </button>
            </p>
          </div>

          {/* Help & Documentation Links */}
          <div className="help-links">
            <p className="help-label">Help & Documentation</p>
            <div className="help-links-grid">
              <a href="/help" className="help-link" target="_blank" rel="noopener noreferrer">
                📖 User Guide
              </a>
              <a href="/help" className="help-link" target="_blank" rel="noopener noreferrer">
                ❓ FAQ
              </a>
              <a href="mailto:support@gavekort.no" className="help-link">
                💬 Support
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Onboarding Modal - Phase 3 */}
      {/* Show modal if user logged in, onboarding not complete, and not loading */}
      {!isSignUp && auth.currentUser && !roleLoading && (
        <OnboardingModal
          isOpen={showOnboardingModal}
          onComplete={handleOnboardingComplete}
        />
      )}
    </>
  );
}

